// Taking over "back" inside a messenger.
//
// A mini app is a page inside someone else's app, and the Android back key
// belongs to the host until the page claims it. Unclaimed, Bale answers a back
// press by collapsing the mini app — including the press that a user makes to
// dismiss the soft keyboard, which is how «back on the keyboard minimises the
// برنامک» happens with nothing wrong on our side of the glass.
//
// All three clients implement Telegram's BackButton contract (verified in the
// shipped bundles: `web_app_setup_back_button` out, `backButtonPressed` back),
// and a visible BackButton is the documented way to say "this page handles
// back". We were declaring the type for it and never calling it.
//
// Wiring it up was not enough on its own: claiming the button needs an SDK, an
// SDK needs to know which messenger this is, and until `resolvePlatform()` that
// answer only survived the launch that fetched it. So the claim below fired on
// the first launch of an install and never again — which is why back still
// minimised the mini app on all three clients after the first fix. See the
// comment on `resolvePlatform` in detect.ts.
//
// Two rules, in order:
//
//   1. If a text field has focus, back dismisses the keyboard and nothing else.
//      That is what the key means on Android, and it is the reported bug.
//   2. Otherwise go back one screen — but only while there is a screen to go
//      back to. At the app's front door the button is hidden again, so back
//      reaches the host and closes the mini app, which is what someone at home
//      pressing back actually wants.
//
// Driven off the router object rather than a hook so it needs no place in the
// component tree: `routes` is a module-level singleton and index.tsx already
// subscribes to it for pageviews.

import type { createBrowserRouter } from 'react-router-dom'

import { historyIndex } from 'utilities/navigation/flow'
import { isMiniapp, resolvePlatform } from './detect'
import { loadSdk } from './sdk'

/** Taken from react-router rather than @remix-run/router, which is transitive. */
type Router = ReturnType<typeof createBrowserRouter>

/** Screens that are the end of the line — nothing above them to go back to. */
const ROOTS = ['/', '/home']

/**
 * How many screens deep we are, relative to where this launch started.
 *
 * Read from react-router's own history index rather than counted from PUSH/POP
 * events. Counting drifted: REPLACE moves you between screens without changing
 * the count, `<Navigate replace>` on `/` fires one on every visit to the root,
 * and any subscriber call that is missed or doubled is a permanent off-by-one.
 * Once the count said 0 while the user was several screens in, back took the
 * "nowhere to go" branch below and bounced them out of their journey.
 *
 * `window.history.length` is not usable for this — it includes whatever the
 * webview did before us and never shrinks.
 */
let baseIndex = 0
const depth = () => Math.max(0, historyIndex() - baseIndex)

const isTextEntry = (el: Element | null): el is HTMLElement => {
    if (!el) return false
    const tag = el.tagName
    return (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        (el as HTMLElement).isContentEditable === true
    )
}

const backButton = async () => {
    const platform = resolvePlatform()
    if (!platform) return null
    const webApp = await loadSdk(platform)
    return webApp?.BackButton ?? null
}

/**
 * Re-apply the button state, from outside.
 *
 * The platform can arrive *after* install — a launch that skips authentication
 * has to ask the backend which messenger this is, and that answer lands a
 * round-trip later. Without this the button would stay unclaimed until the next
 * navigation, and on a client we cannot sniff, for the whole session.
 */
let resync: (() => void) | null = null
export const resyncMiniappBackButton = () => resync?.()

export const installMiniappBackButton = (router: Router) => {
    if (!isMiniapp()) return

    const goBack = () => {
        // The keyboard first. `blur()` is what closes it, and swallowing this
        // one press is the whole point of claiming the button.
        const active = document.activeElement
        if (isTextEntry(active)) {
            active.blur()
            return
        }
        if (depth() > 0) {
            router.navigate(-1)
            return
        }
        // Nothing of ours left to go back to, but we are not on a root screen
        // either — a deep link, typically. Send them home.
        //
        // `replace`, emphatically. A push here put a new entry on top of the
        // screen we were trying to leave, so the next back popped straight back
        // onto it, and the one after that pushed /home again: صندوق‌ها → home →
        // صندوق‌ها → home, forever, with no way out but killing the app.
        if (!ROOTS.includes(router.state.location.pathname)) {
            router.navigate('/home', { replace: true })
        }
    }

    // A focused text field counts even at the front door. Otherwise the very
    // press this whole file exists for — back to dismiss the keyboard, on the
    // home screen — is still handed to the host, which minimises the mini app.
    const wantsButton = () =>
        isTextEntry(document.activeElement) ||
        depth() > 0 ||
        !ROOTS.includes(router.state.location.pathname)

    // Which client's button we attached the handler to. Keyed by platform, not
    // a bare boolean: if the backend later corrects a sniffed platform, the
    // handler has to move to the *new* client's button.
    let wiredTo: string | null = null
    let shown: boolean | null = null

    const sync = () => {
        const wanted = wantsButton()
        const platform = resolvePlatform()
        // Note the state is recorded only once it has actually been applied.
        // Recording the intent up front meant that a sync which ran before any
        // SDK existed marked the button as already-in-that-state, and the retry
        // after the platform arrived was skipped as redundant.
        if (wanted === shown && platform === wiredTo) return
        backButton()
            .then((button) => {
                if (!button || !platform) return
                if (platform !== wiredTo) {
                    wiredTo = platform
                    button.onClick(goBack)
                }
                if (wanted) button.show()
                else button.hide()
                shown = wanted
            })
            .catch(() => {
                // An old client without the method, or an SDK that never
                // loaded. Back keeps whatever behaviour the host gives it.
            })
    }

    resync = sync

    // Where this launch started. Everything above it is ours to go back
    // through; at it, back belongs to the messenger again.
    baseIndex = historyIndex()

    router.subscribe(sync)

    // focusout lands before the next focusin, so `activeElement` is still
    // settling; a tick later it is either <body> or the next field, and either
    // answer is the right one.
    document.addEventListener('focusin', sync)
    document.addEventListener('focusout', () => window.setTimeout(sync, 0))

    // subscribe() only fires on changes, and the launch route is already
    // decided by the time we get here.
    sync()
}
